/**
 * Pure logic for Tree (guild → category → channel). Unit-tested in models.test.ts.
 *
 * Selection model: the selected set holds the ids of **selectable channels** (enabled
 * `kind: "channel"` nodes without children). A parent's checkbox is derived: all / some /
 * none of its selectable descendant channels. Toggling a parent selects or clears all of them.
 */
import type { ReactNode } from "react";

export type TreeNodeKind = "guild" | "category" | "channel";

export interface TreeNode {
  id: string;
  label: string;
  kind: TreeNodeKind;
  children?: TreeNode[];
  /** Not selectable (e.g. channel the account cannot read). Still focusable. */
  disabled?: boolean;
  /** Trailing content: badges, counts. */
  meta?: ReactNode;
  /** Optional icon override (e.g. guild avatar). */
  icon?: ReactNode;
}

export interface FlatTreeItem {
  node: TreeNode;
  level: number; // 1-based (aria-level)
  parentId: string | null;
  posinset: number; // 1-based
  setsize: number;
  hasChildren: boolean;
  expanded: boolean;
}

export type CheckState = "checked" | "unchecked" | "mixed";

export function hasChildren(node: TreeNode): boolean {
  return !!node.children && node.children.length > 0;
}

/** Depth-first list of the rows currently visible given the expanded set. */
export function flattenVisible(
  nodes: readonly TreeNode[],
  expanded: ReadonlySet<string>,
): FlatTreeItem[] {
  const out: FlatTreeItem[] = [];
  const walk = (list: readonly TreeNode[], level: number, parentId: string | null) => {
    list.forEach((node, i) => {
      const parent = hasChildren(node);
      const isOpen = parent && expanded.has(node.id);
      out.push({
        node,
        level,
        parentId,
        posinset: i + 1,
        setsize: list.length,
        hasChildren: parent,
        expanded: isOpen,
      });
      if (isOpen) walk(node.children ?? [], level + 1, node.id);
    });
  };
  walk(nodes, 1, null);
  return out;
}

/** Selectable leaf ids under (and including) a node. */
export function selectableLeafIds(node: TreeNode): string[] {
  // Empty guilds/categories are not leaves: only channels are ever selected.
  if (!hasChildren(node)) return node.disabled || node.kind !== "channel" ? [] : [node.id];
  if (node.disabled) return [];
  return (node.children ?? []).flatMap(selectableLeafIds);
}

/** Every selectable leaf id in the forest. */
export function allSelectableLeafIds(nodes: readonly TreeNode[]): string[] {
  return nodes.flatMap(selectableLeafIds);
}

export function checkState(node: TreeNode, selected: ReadonlySet<string>): CheckState {
  const leaves = selectableLeafIds(node);
  if (leaves.length === 0) return "unchecked";
  let hit = 0;
  for (const id of leaves) if (selected.has(id)) hit++;
  if (hit === 0) return "unchecked";
  return hit === leaves.length ? "checked" : "mixed";
}

/** Toggle a node: fully checked → clear its leaves; otherwise select all its leaves. */
export function toggleNode(node: TreeNode, selected: ReadonlySet<string>): Set<string> {
  const leaves = selectableLeafIds(node);
  const next = new Set(selected);
  if (checkState(node, selected) === "checked") for (const id of leaves) next.delete(id);
  else for (const id of leaves) next.add(id);
  return next;
}

/** Ids of every node that has children (for "expand all"). */
export function parentIds(nodes: readonly TreeNode[]): string[] {
  const out: string[] = [];
  const walk = (list: readonly TreeNode[]) => {
    for (const n of list) {
      if (hasChildren(n)) {
        out.push(n.id);
        walk(n.children ?? []);
      }
    }
  };
  walk(nodes);
  return out;
}

/**
 * Type-ahead: the next visible item after `fromIndex` whose label starts with `query`
 * (case-insensitive), wrapping around. Returns -1 if none.
 */
export function typeaheadIndex(items: readonly FlatTreeItem[], fromIndex: number, query: string): number {
  const q = query.toLocaleLowerCase();
  if (!q) return -1;
  for (let step = 1; step <= items.length; step++) {
    const i = (fromIndex + step) % items.length;
    if (items[i].node.label.toLocaleLowerCase().startsWith(q)) return i;
  }
  return -1;
}
